<template>
  <v-card class="member-card pa-4" outlined rounded="xl" :style="themeStyle">
    <v-row no-gutters align="start">
      <v-avatar
        size="64"
        class="mr-4"
        :color="student.avatar ? '' : theme.primary"
      >
        <v-img
          v-if="student.avatar"
          :src="student.avatar"
          cover
          class="member-card__avatar-image"
        />
        <span v-else class="white--text font-weight-bold">
          {{ initials }}
        </span>
      </v-avatar>

      <v-col>
        <div class="d-flex justify-space-between align-start flex-wrap gap-2">
          <div>
            <div class="member-card__name">
              {{ student.name || "Aluno(a) sem identificação" }}
            </div>
            <div class="caption grey--text text--darken-1 mt-1">
              <v-icon small class="mr-1" :color="theme.secondary">
                mdi-email-outline
              </v-icon>
              {{ student.email || "E-mail indisponível" }}
            </div>
          </div>

          <v-chip x-small dark :color="statusColor">
            {{ statusLabel }}
          </v-chip>
        </div>

        <div v-if="student.bio" class="caption mt-3 member-card__bio">
          {{ student.bio }}
        </div>
      </v-col>
    </v-row>

    <div class="mt-4 d-flex flex-wrap">
      <v-chip small outlined class="mr-2 mb-2" :color="theme.primary">
        <v-icon small left>mdi-chart-bar</v-icon>
        {{ levelLabel }}
      </v-chip>

      <v-chip small outlined class="mr-2 mb-2" :color="theme.secondary">
        <v-icon small left>mdi-refresh</v-icon>
        {{ frequencyLabel }}
      </v-chip>

      <v-chip small outlined class="mb-2" :color="theme.primary">
        <v-icon small left>mdi-account-group-outline</v-icon>
        {{ formatLabel }}
      </v-chip>
    </div>

    <div v-if="showLevelEditor" class="mt-1">
      <v-btn
        small
        text
        rounded
        class="member-card__edit-level px-0"
        :color="theme.primary"
        :loading="loadingLevelUpdate"
        @click="openLevelDialog"
      >
        <v-icon small left>mdi-pencil-outline</v-icon>
        Alterar nível do aluno
      </v-btn>
    </div>

    <v-card v-if="showBilling" flat class="member-card__billing mt-4 pa-3">
      <div class="caption text-uppercase font-weight-bold grey--text">
        Mensalidade
      </div>
      <div class="member-card__billing-value">
        {{ billingValue || "A combinar" }}
      </div>
      <div
        v-if="billingDescription"
        class="caption mt-1 member-card__billing-description"
      >
        {{ billingDescription }}
      </div>

      <div v-if="showBillingEditor" class="mt-4">
        <v-select
          v-model="selectedBillingChoice"
          :items="billingChoices"
          item-text="text"
          item-value="value"
          label="Plano de ensino do aluno"
          outlined
          dense
          hide-details="auto"
        />

        <v-text-field
          v-if="isCustomBillingSelected"
          v-model="customBillingValue"
          class="mt-3"
          label="Valor customizado mensal"
          prefix="R$"
          outlined
          dense
          hide-details="auto"
        />

        <div class="d-flex justify-end mt-3">
          <v-btn
            small
            rounded
            :color="theme.primary"
            class="white--text"
            :loading="loadingBillingUpdate"
            :disabled="!canSaveBilling"
            @click="saveBillingUpdate"
          >
            Salvar cobrança
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-card-actions v-if="showActions" class="px-0 pt-4 pb-0 flex-wrap">
      <v-btn
        v-if="student.status === 0"
        class="mr-2 mb-2"
        dark
        rounded
        :color="theme.primary"
        :loading="loadingSave"
        @click="openDialog(student.id)"
      >
        <v-icon small left>mdi-check-circle-outline</v-icon>
        Aceitar aluno
      </v-btn>

      <v-btn
        v-if="student.status === 0"
        class="mb-2"
        outlined
        rounded
        color="error"
        :loading="loadingReject"
        @click="reject(student.id)"
      >
        <v-icon small left>mdi-close-circle-outline</v-icon>
        Recusar aluno
      </v-btn>

      <v-btn
        v-if="student.status === 1"
        class="mr-2 mb-2"
        outlined
        rounded
        :color="theme.primary"
        :loading="loadingSave"
      >
        <v-icon small left>mdi-swap-horizontal</v-icon>
        Trocar de turma
      </v-btn>

      <v-btn
        v-if="student.status === 1"
        class="mb-2"
        outlined
        rounded
        color="error"
        :loading="loadingReject"
        @click="reject(student.id)"
      >
        <v-icon small left>mdi-delete-outline</v-icon>
        Remover aluno
      </v-btn>
    </v-card-actions>

    <confirm-student-dialog
      v-if="showActions"
      ref="confirmDialog"
      :title="'Aceitar '"
      :items="classDays"
      :selectedItem="student"
      :displayDates="normalizedTimes"
      @confirmed="admit"
    />

    <v-dialog v-if="showLevelEditor" v-model="levelDialog" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Atualizar nível do aluno</v-card-title>
        <v-card-text>
          <div class="caption grey--text text--darken-1 mb-4">
            Selecione o nível atual do aluno nesta turma.
          </div>

          <v-select
            v-model="selectedLevel"
            :items="levelOptions"
            item-text="text"
            item-value="value"
            label="Nível do aluno"
            outlined
            dense
            hide-details="auto"
          />

          <div class="mt-4">
            <div class="caption grey--text text--darken-1 mb-2">
              Atualizar o mesmo nível para:
            </div>
            <v-checkbox
              v-for="sibling in siblingStudents"
              :key="sibling.id"
              v-model="cascadeStudentIds"
              :label="sibling.name"
              :value="sibling.id"
              dense
              hide-details
              class="mt-0"
            />
            <v-checkbox
              v-model="cascadeGroup"
              label="Turma (nível pedagógico da turma)"
              dense
              hide-details
              class="mt-0"
            />
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="closeLevelDialog">Cancelar</v-btn>
          <v-btn
            dark
            rounded
            :color="theme.primary"
            :loading="loadingLevelUpdate"
            :disabled="
              selectedLevel === null ||
              (+selectedLevel === +student.level &&
                cascadeStudentIds.length === 0 &&
                !cascadeGroup)
            "
            @click="saveLevelUpdate"
          >
            Salvar nível
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import {
  ENGLISH_LEVELS,
  displayGroupFormat,
  displayGroupFrequency,
  displayGroupLevel,
  initialsFromName,
  participantStatusColor,
  participantStatusLabel,
  resolveBillingValue,
} from "@/shared/classPresentation";

export default {
  name: "ClassMemberCard",

  props: {
    student: {
      type: Object,
      required: true,
    },
    theme: {
      type: Object,
      required: true,
    },
    normalizedTimes: {
      type: Array,
      default: () => [],
    },
    classDays: {
      type: Array,
      default: () => [],
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showBilling: {
      type: Boolean,
      default: false,
    },
    showBillingEditor: {
      type: Boolean,
      default: false,
    },
    showLevelEditor: {
      type: Boolean,
      default: false,
    },
    partnerPlans: {
      type: Array,
      default: () => [],
    },
    levelOptions: {
      type: Array,
      default: () => ENGLISH_LEVELS,
    },
    groupStudents: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loadingSave: false,
      loadingReject: false,
      loadingLevelUpdate: false,
      loadingBillingUpdate: false,
      levelDialog: false,
      selected: null,
      selectedLevel: null,
      selectedBillingChoice: "__none",
      customBillingValue: null,
      cascadeStudentIds: [],
      cascadeGroup: false,
    };
  },

  watch: {
    student: {
      immediate: true,
      deep: true,
      handler() {
        this.syncBillingForm();
      },
    },
    "student.level": {
      immediate: true,
      handler(level) {
        this.selectedLevel =
          level === undefined || level === null ? null : +level;
      },
    },
  },

  computed: {
    siblingStudents() {
      return (this.groupStudents || []).filter(
        (item) => item.id !== this.student.id
      );
    },
    themeStyle() {
      return {
        "--member-primary": this.theme.primary,
        "--member-secondary": this.theme.secondary,
        "--member-surface": this.theme.surfaceAlt || this.theme.surface,
        "--member-border": this.theme.border,
      };
    },
    initials() {
      return initialsFromName(this.student.name);
    },
    levelLabel() {
      return displayGroupLevel(this.student.level);
    },
    frequencyLabel() {
      return displayGroupFrequency(this.student.frequency);
    },
    formatLabel() {
      return displayGroupFormat(this.student.format);
    },
    statusLabel() {
      return participantStatusLabel(this.student.status);
    },
    statusColor() {
      return participantStatusColor(this.student.status);
    },
    billingValue() {
      return resolveBillingValue(this.student);
    },
    billingDescription() {
      if (this.student?.billing?.mode === "CUSTOM") {
        return "Valor customizado definido pelo professor.";
      }

      if (this.student?.currentPlan) {
        return `${displayGroupFormat(
          this.student.currentPlan.format
        )} - ${displayGroupFrequency(this.student.currentPlan.frequency)}`;
      }

      return "Cobrança ainda não definida para este aluno.";
    },
    billingChoices() {
      const plans = Array.isArray(this.partnerPlans) ? this.partnerPlans : [];
      const options = [
        ...plans.map((plan) => ({
          text: `${displayGroupFormat(plan.format)} - ${displayGroupFrequency(
            plan.frequency
          )} - ${this.formatCurrencyValue(plan.finalValue ?? plan.value)}/mes`,
          value: plan.id,
        })),
      ];

      if (
        this.selectedBillingChoice &&
        !["__none", "__custom"].includes(this.selectedBillingChoice) &&
        !options.find((option) => option.value === this.selectedBillingChoice)
      ) {
        options.splice(1, 0, {
          text: "Plano atualmente vinculado",
          value: this.selectedBillingChoice,
        });
      }

      return options;
    },
    currentBillingChoice() {
      if (this.student?.billing?.mode === "CUSTOM") {
        return "__custom";
      }

      return this.student?.billing?.teachingPlanId || "__none";
    },
    currentCustomBillingValue() {
      if (this.student?.billing?.mode !== "CUSTOM") {
        return null;
      }

      return this.normalizeCurrencyInput(
        this.student?.billing?.customMonthlyValue
      );
    },
    isCustomBillingSelected() {
      return this.selectedBillingChoice === "__custom";
    },
    isBillingDirty() {
      if (this.selectedBillingChoice !== this.currentBillingChoice) {
        return true;
      }

      if (!this.isCustomBillingSelected) {
        return false;
      }

      return (
        this.normalizeCurrencyInput(this.customBillingValue) !==
        this.currentCustomBillingValue
      );
    },
    canSaveBilling() {
      if (!this.showBillingEditor || this.loadingBillingUpdate) {
        return false;
      }

      if (this.isCustomBillingSelected) {
        return (
          this.normalizeCurrencyInput(this.customBillingValue) !== null &&
          this.isBillingDirty
        );
      }

      return this.isBillingDirty;
    },
  },

  methods: {
    formatCurrencyValue(value) {
      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return "R$ 0,00";
      }

      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(numericValue);
    },
    normalizeCurrencyInput(value) {
      if (value === undefined || value === null || value === "") {
        return null;
      }

      const rawValue = String(value).trim();
      const sanitizedValue = rawValue.includes(",")
        ? rawValue.replace(/\./g, "").replace(",", ".")
        : rawValue;
      const normalizedValue = Number(sanitizedValue);

      if (Number.isNaN(normalizedValue) || normalizedValue <= 0) {
        return null;
      }

      return Number(normalizedValue.toFixed(2));
    },
    syncBillingForm() {
      this.selectedBillingChoice = this.currentBillingChoice;
      this.customBillingValue =
        this.currentCustomBillingValue === null
          ? null
          : `${this.currentCustomBillingValue}`;
    },
    openLevelDialog() {
      this.selectedLevel =
        this.student.level === undefined || this.student.level === null
          ? null
          : +this.student.level;
      this.cascadeStudentIds = [];
      this.cascadeGroup = false;
      this.levelDialog = true;
    },
    closeLevelDialog() {
      this.levelDialog = false;
      this.selectedLevel =
        this.student.level === undefined || this.student.level === null
          ? null
          : +this.student.level;
    },
    openDialog(selected) {
      this.selected = selected;
      this.$refs.confirmDialog.open();
    },
    async saveLevelUpdate() {
      const levelUnchanged = +this.selectedLevel === +this.student.level;
      const hasCascade = this.cascadeStudentIds.length > 0 || this.cascadeGroup;
      if (this.selectedLevel === null || (levelUnchanged && !hasCascade)) {
        return;
      }

      this.loadingLevelUpdate = true;

      return await partnerApi
        .updateStudentEnglishLevel(this.$route.params.id, this.student.id, {
          level: +this.selectedLevel,
          studentIds: [...this.cascadeStudentIds],
          updateGroup: this.cascadeGroup,
        })
        .then(() => {
          this.$root.$emit("display-alert", {
            text: "Nível do aluno atualizado com sucesso.",
            type: 0,
          });
          this.closeLevelDialog();
          this.$emit("reload");
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: "Nao foi possivel atualizar o nivel do aluno.",
            type: 1,
          });
        })
        .finally(() => {
          this.loadingLevelUpdate = false;
        });
    },
    async saveBillingUpdate() {
      let payload = {
        mode: "NONE",
      };

      if (this.isCustomBillingSelected) {
        const customMonthlyValue = this.normalizeCurrencyInput(
          this.customBillingValue
        );

        if (customMonthlyValue === null) {
          this.$root.$emit("display-alert", {
            text: "Informe um valor mensal válido para a cobrança customizada.",
            type: 1,
          });
          return;
        }

        payload = {
          mode: "CUSTOM",
          customMonthlyValue,
        };
      } else if (this.selectedBillingChoice !== "__none") {
        payload = {
          mode: "PLAN",
          teachingPlanId: this.selectedBillingChoice,
        };
      }

      this.loadingBillingUpdate = true;

      return await partnerApi
        .updateStudentBilling(this.$route.params.id, this.student.id, payload)
        .then(() => {
          this.$root.$emit("display-alert", {
            text: "Cobrança do aluno atualizada com sucesso.",
            type: 0,
          });
          this.$emit("reload");
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: "Nao foi possivel atualizar a cobrança do aluno.",
            type: 1,
          });
        })
        .finally(() => {
          this.loadingBillingUpdate = false;
        });
    },
    async admit(weekDay) {
      this.loadingSave = true;

      return await partnerApi
        .updateRequest(this.$route.params.id, {
          customer: this.selected,
          status: 1,
          weekDay,
        })
        .then(() => {
          this.$root.$emit("display-alert", {
            text: "Ótimo, você aceitou o aluno na sua turma!",
            type: 0,
          });
          this.$refs.confirmDialog.close();
          this.$emit("reload");
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: "Ocorreu um erro inesperado, tente novamente mais tarde.",
            type: 1,
          });
        })
        .finally(() => {
          this.loadingSave = false;
        });
    },
    async reject(id) {
      this.loadingReject = true;

      return await partnerApi
        .updateRequest(this.$route.params.id, {
          customer: id,
          status: 3,
        })
        .then(() => {
          this.$root.$emit("display-alert", {
            text: "A entrada do aluno na turma foi recusada.",
            type: 1,
          });
          this.$emit("reload");
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: "Ocorreu um erro inesperado, tente novamente mais tarde.",
            type: 1,
          });
        })
        .finally(() => {
          this.loadingReject = false;
        });
    },
  },
};
</script>

<style scoped lang="css">
.member-card {
  background: linear-gradient(
    180deg,
    var(--member-surface) 0%,
    rgba(255, 255, 255, 0.96) 100%
  );
  border: 1px solid var(--member-border) !important;
  box-shadow: 0 20px 40px -30px rgba(15, 23, 42, 0.35);
}

.member-card__name {
  color: #1f2937;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
}

.member-card__bio {
  color: #4b5563;
  line-height: 1.5;
}

.member-card__avatar-image {
  width: 100%;
  height: 100%;
}

.member-card__billing {
  background: rgba(255, 255, 255, 0.92);
  border-radius: 16px;
  border: 1px solid var(--member-border);
}

.member-card__billing-value {
  color: var(--member-primary);
  font-size: 1.15rem;
  font-weight: 700;
}

.member-card__billing-description {
  color: #4b5563;
  line-height: 1.45;
}

.member-card__edit-level {
  text-transform: none;
}

.gap-2 {
  gap: 8px;
}
</style>
